import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Copy, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const CODE = "Schnuppergolf20";

const LEVELS = [
  { lvl: 1, title: "Schnupperkurs", text: "Gerade bestanden – Glückwunsch! Dein Einstieg ins Golfspiel ist geschafft.", done: true },
  { lvl: 2, title: "Schnupperkurs PLUS", text: "Inklusive Range-Reife und gemeinsamem Essen – der perfekte nächste Schritt nach Deinem ersten Erfolg." },
  { lvl: 3, title: "Platzreife", text: "Intensiv oder entspannt – Du entscheidest das Tempo. Dein offizieller Weg auf den Golfplatz." },
  { lvl: 4, title: "Newcomer Kurs", text: "Dein regelmäßiges Training – jeden Dienstag um 18:30 Uhr. Dranbleiben, besser werden, Anschluss finden." },
];

export default function NachDemSchnupperkurs() {
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(CODE); } catch { /* Clipboard ggf. blockiert */ }
    try { sessionStorage.setItem("gca_promo", CODE); } catch { }
    setCopied(true);
    toast.success("Rabattcode kopiert – er wird beim Buchen automatisch vorausgefüllt!");
  };

  const bookNow = async () => {
    await copyCode();
    nav("/kurse");
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="nachdemschnupperkurs-funnel">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 45% at 50% 0%, rgba(255,0,51,0.16) 0%, rgba(0,0,0,0) 60%)" }} />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-12 lg:py-16 flex flex-col items-center text-center">
        <img src="/logo.png" alt="Golfcoaching Academy Wendlingen" className="h-14 w-auto mx-auto mb-8"
             onError={(e) => { e.currentTarget.outerHTML = '<div class="font-heading text-xl uppercase tracking-tight mb-8">Golfcoaching Academy</div>'; }} />

        <div className="inline-flex items-center gap-2 border border-[#ff0033]/50 text-[#ff0033] px-3 py-1.5 font-mono-lab text-[10px] uppercase tracking-widest mb-6">
          <PartyPopper size={13} /> Glückwunsch · Level 1 geschafft
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight" data-testid="nds-headline">
          Schnupperkurs bestanden<br /><span className="text-[#ff0033]">– was nun?</span>
        </h1>

        <p className="text-neutral-300 text-base md:text-lg mt-5 max-w-lg mx-auto">
          Du hast den ersten Schritt gemacht. So geht Dein Weg im System der Golfcoaching Academy weiter:
        </p>

        {/* Road to Tiger */}
        <div className="mt-10 w-full">
          <p className="font-mono-lab text-[10px] uppercase tracking-[0.25em] text-[#ff0033]">// DEIN WEG</p>
          <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight mt-2" data-testid="nds-road-title">
            „Road to Tiger"
          </h2>
          <p className="text-neutral-300 text-sm md:text-base mt-1">Golf mit Spaß richtig lernen</p>
        </div>

        {/* Level-Blöcke (bestanden oben, dann aufsteigend) */}
        <div className="mt-6 w-full space-y-3 text-left" data-testid="nds-levels">
          {LEVELS.map((l) => (
            <div key={l.lvl} data-testid={`nds-level-${l.lvl}`}
              className={`border p-5 sm:p-6 flex items-start gap-4 ${l.done ? "border-green-500/50 bg-green-500/5" : "border-white/15 bg-white/[0.03] hover:border-[#ff0033]/60 transition-colors"}`}>
              <div className={`shrink-0 w-11 h-11 flex items-center justify-center font-heading text-lg ${l.done ? "bg-green-500 text-black" : "bg-[#ff0033] text-white"}`}>
                {l.done ? <Check size={22} /> : l.lvl}
              </div>
              <div>
                <p className={`font-mono-lab text-[10px] uppercase tracking-widest ${l.done ? "text-green-400" : "text-[#ff0033]"}`}>Level {l.lvl}{l.done ? " · Bestanden" : ""}</p>
                <h3 className="font-heading text-xl sm:text-2xl uppercase tracking-tight mt-0.5">{l.title}</h3>
                <p className="text-neutral-400 text-sm mt-1.5 leading-relaxed">{l.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Hinweis: Level überspringbar */}
        <div className="mt-4 w-full border border-white/15 bg-white/[0.03] p-5 text-left" data-testid="nds-skip-note">
          <p className="text-neutral-300 text-sm leading-relaxed">
            <span className="text-white font-medium">Übrigens:</span> Die „Road to Tiger" ist Dein Wegweiser – kein Pflichtprogramm.
            Level können natürlich auch übersprungen werden. Du kannst z.&nbsp;B. direkt nach dem Schnupperkurs in die Platzreife starten
            oder gleich in den Newcomer Kurs einsteigen – ganz so, wie es zu Deinem Tempo, Deiner Zeit und Deinen Zielen passt.
          </p>
        </div>

        {/* Rabattcode */}
        <div className="mt-12 w-full border-2 border-[#ff0033] bg-[#ff0033]/5 p-6 sm:p-8" data-testid="nds-code-box">
          <p className="font-heading text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
            20 % Rabatt<span className="text-[#ff0033]"> für Deinen nächsten Kurs</span>
          </p>
          <button onClick={copyCode} data-testid="nds-code" title="Code kopieren"
            className="mt-5 inline-flex items-center gap-3 bg-black border border-white/25 px-6 sm:px-10 py-4 font-mono-lab text-xl sm:text-2xl tracking-[0.15em] text-white hover:border-[#ff0033] transition-colors">
            {CODE} {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} className="text-neutral-400" />}
          </button>
          <p className="text-neutral-400 text-xs mt-4 max-w-md mx-auto">
            Einmalig pro Person einlösbar (eine Nutzung je E-Mail-Adresse). Gilt für alle Kurse außer Platzreife-Kurse. Einfach bei der Buchung im Feld „Rabattcode" einlösen – der Rabatt wird sofort abgezogen.
          </p>
        </div>

        {/* CTA */}
        <button onClick={bookNow} data-testid="nds-book-btn"
          className="mt-8 w-full sm:w-auto bg-[#ff0033] text-white font-heading text-2xl uppercase tracking-wider px-14 py-5 hover:bg-[#cc0029] transition-colors inline-flex items-center justify-center gap-3">
          Jetzt buchen <ArrowRight size={24} />
        </button>
        <p className="text-neutral-500 text-xs mt-3">Dein Code wird automatisch kopiert und beim Buchen vorausgefüllt.</p>

        <p className="text-neutral-600 text-[11px] mt-10">Golfcoaching Academy Wendlingen · GC Kirchheim-Wendlingen</p>
      </div>
    </div>
  );
}
